import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import DocumentUpload from "../../components/DocumentUpload";
import InputField from "../../components/InputField";
import DropDown from "../../components/DropDown";
import SearchableDropDown from "../../components/SearchableDropDown";
import { getStateFromGST } from "../../constants/gstStateCodes";
import { toast } from "react-toastify";
import {
  validatePAN,
  validateGST,
  validateMobile,
  validateIFSC,
  validateAccountNumber,
  validateAadhar,
} from "../../utils/validationUtils";
import {
  validateGSTPANMatch,
  validateMultipleGSTWithPAN,
} from "../../utils/gstPanValidation";
import { validatePANEntityType } from "../../utils/panEntityValidation";
import { uploadOnboardingDocumentAction } from "../../actions/uploadOnboardingDocumentAction";
import { convertToBoolean } from "../../utils/dataConversionUtils";
import { getUserData } from "../../utils/authnicate";
import { fetchKYCDetailsByIdAction } from "../../actions/fetchKYCDetailsByIdAction";
import { useAadharVisibilityData } from "./AadharVisibilityData";
import {
  ENTITY_INFO_FIELDS,
  ENTITY_INFO_FIELDS_BEFORE_GST,
  ENTITY_INFO_FIELDS_AFTER_GST,
  GST_FIELDS,
  MSME_FIELDS,
  CONTACT_FIELDS,
  BANK_FIELDS,
  ALL_FIELDS,
  UTILITY_FIELDS,
  THIRD_PARTY_FIELDS,
  ENTITY_TYPE_DOCUMENTS,
  DOCS_BANK,
  DOC_KEY_TO_API_TYPE,
  getCityOptions,
} from "./UploadDetailsFields";
import { sendEntityKYCFormDataAction } from "../../actions/entityKYCFormDataAction";
import { parseDeficiencies } from "../../utils/deficiencyUtils";
// usePincodeAutofill disabled — state/city selected via dropdowns
// import usePincodeAutofill from "../../hooks/usePincodeAutofill";

export {
  ENTITY_INFO_FIELDS,
  ENTITY_INFO_FIELDS_BEFORE_GST,
  ENTITY_INFO_FIELDS_AFTER_GST,
  GST_FIELDS,
  MSME_FIELDS,
  CONTACT_FIELDS,
  BANK_FIELDS,
  UTILITY_FIELDS,
  THIRD_PARTY_FIELDS,
  ENTITY_TYPE_DOCUMENTS,
  DOCS_BANK,
};

// ── Entity-type required documents section ────────────────────────────────────

const DocFileIcon = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#2563eb"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
    <polyline points="10 9 9 9 8 9" />
  </svg>
);

const DocUserIcon = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#2563eb"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const DocCloudIcon = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#2563eb"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M17.5 19H9a7 7 0 1 1 6.71-9h1.79a4.5 4.5 0 1 1 0 9Z" />
    <polyline points="12 13 12 9 10 11" />
  </svg>
);

export const EntityDocumentsSection = ({
  entityType,
  entityFiles,
  onEntityFileChange,
  entityDocInputs = {},
  onEntityDocInputChange,
  onEntityDocInputBlur,
  existingDocuments = [],
  errors = {},
  deficiencyMap = {},
  isDeficiencyMode = false,
  existingPaths = {},
  onViewDocument,
  isDocUploading,
  uploadingDocKeys,
  isGSTRegistered,
}) => {
  const docs = ENTITY_TYPE_DOCUMENTS[entityType];
  if (!docs || docs.length === 0) return null;

  const isGSTYes = isGSTRegistered === "yes";

  const getExisting = (key) => {
    const doc = existingDocuments.find((d) => d.key === key);
    const existing = doc?.files?.find((f) => f.isUploaded);
    if (existing) return existing;
    if (key === "aadharCardBackPath" || key === "AadharCardBackPath") {
      const fallbackPath =
        existingPaths?.AadharCardBackPath ||
        existingPaths?.aadharCardBackPath ||
        "";
      if (fallbackPath) {
        return {
          isUploaded: true,
          fileName: fallbackPath.split(/[\\/]/).pop() || "Aadhar Card (Back)",
          filePath: fallbackPath,
        };
      }
    }
    return null;
  };

  return (
    <div className="entity-docs-section">
      <p className="entity-docs-note">
        Required documents for <strong>{entityType.replace(/_/g, " ")}</strong>
      </p>
      <div className="entity-docs-list">
        {docs.map((doc) => {
          const isDeficient = !!deficiencyMap[doc.key];
          const uploadable = !isDeficiencyMode || isDeficient;
          const fieldName = doc.inputField?.name;
          const existingFile = getExisting(doc.key);

          // Aadhar number inputs — ALWAYS editable and ALWAYS blank on load
          // Never auto-filled, never readonly — user must type fresh every time
          const isAadharInputField =
            fieldName &&
            [
              "doc_aadhar_number",
              "doc_aadharBoth_numbers",
              "doc_aadharBothSecondary_numbers",
              "doc_directorAadhar_number",
              "doc_aadharTrustee_number",
            ].includes(fieldName);

          // Aadhar fields: always editable regardless of deficiency/readonly mode
          const inputEditable = isAadharInputField || uploadable;

          // Icon matching reference image
          const isPersonField = /aadhar|director|partner|trustee|person/i.test(
            doc.label || "",
          );
          const inputIcon = isPersonField ? <DocUserIcon /> : <DocFileIcon />;
          const uploadIcon = !doc.inputField ? (
            <DocFileIcon />
          ) : (
            <DocCloudIcon />
          );

          // Check if field is Aadhaar or PAN document / input
          const isAadharBackDoc =
            doc.key === "aadharCardBackPath" || doc.key === "doc_aadhar_back";

          const isAadharDoc =
            doc.key === "doc_aadhar" ||
            doc.key === "doc_aadharBoth" ||
            doc.key === "doc_directorAadhar" ||
            doc.key === "doc_aadharTrustee" ||
            /aadhar/i.test(doc.key || "") ||
            /aadhar/i.test(doc.label || "");

          const isPanDoc = doc.key === "doc_pan" || doc.key === "doc_panTrust";

          const isAadharInput = Boolean(
            fieldName && (isAadharInputField || /aadhar/i.test(fieldName)),
          );

          const isPrimaryPanInput = Boolean(
            fieldName &&
            (fieldName === "doc_pan_number" ||
              fieldName === "doc_panTrust_number"),
          );

          // 1. PAN Card Number is ALWAYS mandatory (with red asterisk *)
          // 2. Aadhaar Number is non-mandatory when GST is Yes, mandatory when GST is No/unselected
          let inputRequired = doc.required;
          if (isPrimaryPanInput) {
            inputRequired = true;
          } else if (isAadharInput) {
            inputRequired = isGSTYes ? false : doc.required;
          }

          // 1. Aadhaar Card Back is ALWAYS non-mandatory (no red asterisk)
          // 2. PAN Card Document & Aadhaar Front Document are non-mandatory when GST is Yes, mandatory when GST is No/unselected
          let uploadRequired = doc.required;
          if (isAadharBackDoc) {
            uploadRequired = false;
          } else if (isAadharDoc || isPanDoc) {
            uploadRequired = isGSTYes ? false : doc.required;
          }

          return (
            <div
              key={doc.key}
              className={`entity-doc-row ${isDeficient ? "entity-doc-row--deficient" : ""} ${!doc.inputField ? "entity-doc-row--upload-only" : ""}`}
            >
              {doc.inputField && (
                <InputField
                  name={fieldName}
                  label={`${doc.label} Number`}
                  icon={inputIcon}
                  type="text"
                  value={entityDocInputs[fieldName] || ""}
                  onChange={
                    inputEditable && onEntityDocInputChange
                      ? (e) => onEntityDocInputChange(fieldName, e.target.value)
                      : undefined
                  }
                  onBlur={
                    inputEditable && onEntityDocInputBlur
                      ? (e) => onEntityDocInputBlur(fieldName, e.target.value)
                      : undefined
                  }
                  placeholder={doc.inputField.placeholder}
                  maxLength={doc.inputField.maxLength}
                  required={inputRequired}
                  error={errors[fieldName] || ""}
                  readOnly={!inputEditable}
                  className={isDeficient ? "input--deficient" : ""}
                />
              )}
              <div
                className={`${isDeficient ? "doc-deficient-wrap" : ""} ${!doc.inputField ? "doc-upload-full" : ""}`}
              >
                <DocumentUpload
                  label={`Upload ${doc.label}`}
                  icon={uploadIcon}
                  name={doc.key}
                  file={entityFiles[doc.key] || null}
                  existingFile={existingFile}
                  onViewExisting={
                    existingFile?.filePath && onViewDocument
                      ? () => onViewDocument(existingFile.filePath, doc.label)
                      : undefined
                  }
                  onChange={(e) => {
                    if (!uploadable) return;
                    onEntityFileChange(doc.key, e.target.files[0]);
                    const isUploadingThisDoc = isDocUploading
                      ? isDocUploading(doc.key)
                      : uploadingDocKeys instanceof Set
                        ? uploadingDocKeys.has(doc.key)
                        : false;
                    if (!uploadable || isUploadingThisDoc) return;
                    const file = e.target.files[0];
                    if (!file) return;
                    onEntityFileChange(doc.key, file);
                  }}
                  required={uploadRequired}
                  error={errors[`entity_doc_${doc.key}`] || ""}
                  disabled={!uploadable}
                  isUploading={
                    isDocUploading
                      ? isDocUploading(doc.key)
                      : uploadingDocKeys instanceof Set
                        ? uploadingDocKeys.has(doc.key)
                        : false
                  }
                />
                {isDeficient && (
                  <span className="doc-deficient-label">
                    ⚠ Deficiency — please re-upload
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ── Field renderer ─────────────────────────────────────────────────────────────

export const renderFields = (
  fields,
  formData,
  errors,
  handleChange,
  handleBlur,
) => {
  const rendered = [];
  let i = 0;
  while (i < fields.length) {
    const field = fields[i];
    const show =
      !field.conditionalOn ||
      formData[field.conditionalOn] === field.conditionalValue;

    // Collect all consecutive fields with the same group tag
    if (field.group) {
      const groupName = field.group;
      const groupFields = [];
      while (i < fields.length && fields[i].group === groupName) {
        groupFields.push(fields[i]);
        i++;
      }
      rendered.push(
        <div
          key={groupName}
          className={`field-group field-group--${groupName}`}
        >
          {groupFields.map((gf) => {
            const gShow =
              !gf.conditionalOn ||
              formData[gf.conditionalOn] === gf.conditionalValue;
            if (!gShow) return null;
            const gValue = formData[gf.name] || "";
            const gError = errors[gf.name] || "";

            // Select fields inside a group (State, City)
            if (gf.type === "select") {
              const currentState = formData.State || formData.state || "";
              // City options depend on the currently selected State
              const resolvedOptions =
                gf.name === "city"
                  ? getCityOptions(currentState)
                  : gf.options || [];
              const placeholderText =
                gf.name === "city" && !currentState
                  ? "Select state first"
                  : `Please Select ${gf.label}`;
              const isDisabled = gf.name === "city" && !currentState;

              return (
                <SearchableDropDown
                  key={gf.name}
                  name={gf.name}
                  label={gf.label}
                  value={gValue}
                  placeholder={placeholderText}
                  onChange={(e) => {
                    if (handleChange) handleChange(e);
                    // When state changes, reset city
                    if (gf.name === "State" && handleChange) {
                      handleChange({ target: { name: "city", value: "" } });
                    }
                  }}
                  options={resolvedOptions}
                  error={gError}
                  required={gf.required}
                  disabled={isDisabled}
                  fullWidth
                />
              );
            }

            return (
              <InputField
                key={gf.name}
                name={gf.name}
                label={gf.label}
                type={gf.type}
                value={gValue}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder={gf.placeholder}
                error={gError}
                required={gf.required}
                maxLength={gf.maxLength}
                readOnly={gf.readOnly}
              />
            );
          })}
        </div>,
      );
      continue;
    }

    if (!show) {
      i++;
      continue;
    }
    const value = formData[field.name] || "";
    const error = errors[field.name] || "";
    if (field.type === "select") {
      if (field.name === "State" || field.name === "city") {
        const currentState = formData.State || formData.state || "";
        const resolvedOptions =
          field.name === "city"
            ? getCityOptions(currentState)
            : field.options || [];
        rendered.push(
          <SearchableDropDown
            key={field.name}
            name={field.name}
            label={field.label}
            value={value}
            placeholder={
              field.name === "city" && !currentState
                ? "Select state first"
                : `Please Select ${field.label}`
            }
            disabled={field.name === "city" && !currentState}
            onChange={(e) => {
              if (handleChange) handleChange(e);
              if (field.name === "State" && handleChange) {
                handleChange({ target: { name: "city", value: "" } });
              }
            }}
            options={resolvedOptions}
            error={error}
            required={field.required}
            fullWidth
          />,
        );
        continue;
      }

      // Resolve options — use entity-type-specific options if available
      const resolvedOptions =
        field.optionsByEntityType?.[formData.entityType] ?? field.options;
      rendered.push(
        <DropDown
          key={field.name}
          name={field.name}
          value={value}
          onChange={handleChange}
          label={field.label}
          options={resolvedOptions}
          error={error}
          required={field.required}
          fullWidth
        />,
      );
    } else {
      rendered.push(
        <InputField
          key={field.name}
          name={field.name}
          label={field.label}
          type={field.type}
          value={value}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder={field.placeholder}
          error={error}
          required={field.required}
          maxLength={field.maxLength}
          rows={field.rows}
          readOnly={field.readOnly}
        />,
      );
    }
    i++;
  }
  return rendered;
};

// ── Initial state ──────────────────────────────────────────────────────────────

export const initialDetailsState = {
  parentName: "",
  tradeName: "",
  address: "",
  pinCode: "",
  city: "",
  State: "",
  natureOfBusiness: "",
  isInvoiceRequired: "",
  entityType: "",
  CompanypanNumber: "",
  isGSTRegistered: "",
  utilityType: "",
  utilityProviderName: "",
  utilityAccountNumber: "",
  utilityAddress: "",
  isMSMERegistered: "",
  msmeNumber: "",
  msmeStatus: "",
  contactPersonName: "",
  contactPersonDesignation: "",
  contactPersonMobile: "",
  contactEmail: "",
  hasBankDetails: "",
  bankName: "",
  accountNumber: "",
  ifscCode: "",
  accountType: "",
  branchName: "",
  beneficiaryName: "",
  thirdPartyRelation: "",
  thirdPartyAccountNumber: "",
  thirdPartyIfscCode: "",
};

// ── Hook ───────────────────────────────────────────────────────────────────────

export const useUploadDetails = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState(initialDetailsState);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // GST entries state
  const [gstEntries, setGstEntries] = useState([
    { gstNumber: "", gstState: "", gstAddress: "" },
  ]);
  const [gstFiles, setGstFiles] = useState([null]);
  const [gstErrors, setGstErrors] = useState([""]);

  // Document file states
  const [utilityFile, setUtilityFile] = useState(null);
  const [msmeFile, setMsmeFile] = useState(null);
  const [thirdPartyFile, setThirdPartyFile] = useState(null);
  const [entityFiles, setEntityFiles] = useState({});
  const [entityDocInputs, setEntityDocInputs] = useState({});
  const [bankFiles, setBankFiles] = useState({});
  const [existingDocuments, setExistingDocuments] = useState([]);
  // Existing paths from API prefill — used as fallback when user doesn't re-upload
  const [existingPaths, setExistingPaths] = useState({});
  // isXxx flags from API — passed through as-is in the payload (not derived from hasPath)
  const [existingIsFlags, setExistingIsFlags] = useState({});
  // kycId from API prefill — passed through in payload
  const [existingKycId, setExistingKycId] = useState(0);
  // Deficiency mode — set when any *Def flag is true in the API record
  const [deficiencyMap, setDeficiencyMap] = useState({});
  const [isDeficiencyMode, setIsDeficiencyMode] = useState(false);

  // Stores upload API responses keyed by documentType
  // e.g. { PassbookOrCancelledChequePath: { id, fileName, filePath, documentType, extension } }
  const [uploadedDocs, setUploadedDocs] = useState({});

  // Active in-flight upload tracking
  const [uploadingDocKeys, setUploadingDocKeys] = useState(new Set());
  // Failed upload tracking
  const [failedUploadKeys, setFailedUploadKeys] = useState(new Set());

  const isDocUploading = (key) => {
    if (!key) return false;
    if (uploadingDocKeys.has(key)) return true;
    for (const activeKey of uploadingDocKeys) {
      if (
        DOC_KEY_TO_API_TYPE[activeKey] === key ||
        DOC_KEY_TO_API_TYPE[key] === activeKey
      ) {
        return true;
      }
    }
    return false;
  };

  const isUploadingDoc = uploadingDocKeys.size > 0;
  const uploadingDocCount = uploadingDocKeys.size;
  const hasFailedUploads = failedUploadKeys.size > 0;

  const handleEntityFileChange = (key, file) => {
    if (!file) return;
    setEntityFiles((prev) => ({ ...prev, [key]: file }));
  };

  const handleEntityDocInputChange = (name, value) => {
    // Aadhar fields: digits only, max 12
    const aadharFields = [
      "doc_aadhar_number",
      "doc_aadharBoth_numbers",
      "doc_aadharBothSecondary_numbers",
      "doc_aadharTrustee_number",
      "doc_directorAadhar_number",
    ];
    if (aadharFields.includes(name)) {
      value = value.replace(/\D/g, "").slice(0, 12);
    }

    // PAN fields: uppercase, max 10
    const panFields = [
      "doc_pan_number",
      "doc_panTrust_number",
      "doc_directorPan_number",
      "doc_panTrustee_number",
    ];
    if (panFields.includes(name)) {
      value = value.toUpperCase().slice(0, 10);
    }

    setEntityDocInputs((prev) => ({ ...prev, [name]: value }));

    if (value && errors[name] && errors[name].includes("is required")) {
      setErrors((prev) => {
        const e = { ...prev };
        delete e[name];
        return e;
      });
    }

    // PAN entity type check & GST-PAN instant validation — runs while typing
    const panEntityFields = ["doc_pan_number", "doc_panTrust_number"];
    if (panEntityFields.includes(name)) {
      const panUpper = value.trim().toUpperCase();

      // Incomplete PAN while typing (< 10 chars) — clear any prior format / mismatch errors
      if (panUpper.length < 10) {
        setErrors((prev) => {
          const e = { ...prev };
          if (
            e[name] &&
            (e[name].includes("GST-PAN mismatch") ||
              e[name].includes("PAN must be") ||
              e[name].includes("does not match selected entity type"))
          ) {
            delete e[name];
          }
          return e;
        });
        setGstErrors((prev) =>
          prev.map((err) =>
            err && err.includes("GST-PAN mismatch") ? "" : err,
          ),
        );
      }

      // Full 10 characters entered — run validations immediately
      if (panUpper.length === 10) {
        // 1. PAN format check
        const panFormatResult = validatePAN(panUpper);
        if (panFormatResult !== true) {
          setErrors((prev) => ({ ...prev, [name]: panFormatResult }));
          return;
        } else {
          setErrors((prev) => {
            const e = { ...prev };
            if (e[name] && e[name].includes("PAN must be")) {
              delete e[name];
            }
            return e;
          });
        }

        // 2. PAN Entity Type Check
        if (formData.entityType) {
          const entityCheck = validatePANEntityType(
            panUpper,
            formData.entityType,
          );
          if (!entityCheck.isValid) {
            setErrors((prev) => ({ ...prev, [name]: entityCheck.message }));
            toast.error(`⚠️ PAN Mismatch: ${entityCheck.message}`, {
              position: "top-right",
              autoClose: 6000,
              toastId: "pan-entity-mismatch",
            });
            return;
          } else {
            setErrors((prev) => {
              const e = { ...prev };
              if (
                e[name] &&
                e[name].includes("does not match selected entity type")
              ) {
                delete e[name];
              }
              return e;
            });
          }
        }

        // 3. GST-PAN instant cross validation when GST is registered
        if (formData.isGSTRegistered === "yes") {
          const activeGstEntries = gstEntries.filter(
            (g) => g.gstNumber && g.gstNumber.trim().length === 15,
          );
          if (activeGstEntries.length > 0) {
            const multiCheck = validateMultipleGSTWithPAN(
              activeGstEntries,
              panUpper,
            );
            if (!multiCheck.isValid && multiCheck.errors.length > 0) {
              const firstError = multiCheck.errors[0];
              setErrors((prev) => ({ ...prev, [name]: firstError.message }));
              toast.error(`⚠️ ${firstError.message}`, {
                position: "top-right",
                autoClose: 6000,
                toastId: "gst-pan-mismatch",
              });
              setGstErrors((prev) => {
                const next = [...prev];
                multiCheck.errors.forEach((err) => {
                  const idx = gstEntries.findIndex(
                    (g) => g.gstNumber === err.gstNumber,
                  );
                  if (idx !== -1) {
                    next[idx] = err.message;
                  }
                });
                return next;
              });
            } else {
              // Matches! Clear GST-PAN mismatch errors from PAN field and from gstErrors
              setErrors((prev) => {
                const e = { ...prev };
                if (e[name] && e[name].includes("GST-PAN mismatch")) {
                  delete e[name];
                }
                return e;
              });
              setGstErrors((prev) =>
                prev.map((err) =>
                  err && err.includes("GST-PAN mismatch") ? "" : err,
                ),
              );
            }
          }
        }
      }
    }
  };

  // Blur validation for document number inputs
  const handleEntityDocInputBlur = (name, value) => {
    if (!value || !value.trim()) {
      const isPrimaryPan =
        name === "doc_pan_number" || name === "doc_panTrust_number";

      // PAN Card Number is ALWAYS mandatory
      if (isPrimaryPan) {
        setErrors((prev) => ({
          ...prev,
          [name]:
            name === "doc_panTrust_number"
              ? "PAN of Trust Number is required"
              : "PAN Card Number is required",
        }));
        return;
      }

      const isAadhar = /aadhar/i.test(name);
      if (formData.isGSTRegistered === "yes" && isAadhar) {
        setErrors((prev) => {
          const e = { ...prev };
          delete e[name];
          return e;
        });
      } else if (formData.isGSTRegistered !== "yes" && formData.entityType) {
        const entityDocs = ENTITY_TYPE_DOCUMENTS[formData.entityType] || [];
        const matchingDoc = entityDocs.find((d) => d.inputField?.name === name);
        if (matchingDoc && matchingDoc.required) {
          setErrors((prev) => ({
            ...prev,
            [name]: `${matchingDoc.label} Number is required`,
          }));
        }
      }
      return;
    }
    const validators = {
      doc_aadhar_number: validateAadhar,
      doc_aadharBoth_numbers: validateAadhar,
      doc_aadharBothSecondary_numbers: validateAadhar,
      doc_aadharTrustee_number: validateAadhar,
      doc_directorAadhar_number: validateAadhar,
      doc_pan_number: validatePAN,
      doc_directorPan_number: validatePAN,
      doc_panTrust_number: validatePAN,
      doc_panTrustee_number: validatePAN,
    };
    const validator = validators[name];
    if (!validator) return;
    const result = validator(value);

    setErrors((prev) => {
      const e = { ...prev };
      if (result !== true) {
        e[name] = result;
        return e;
      }
      delete e[name];

      // Cross-check: PAN and Director PAN must not be the same
      const panValue =
        name === "doc_pan_number"
          ? value.trim().toUpperCase()
          : (entityDocInputs["doc_pan_number"] || "").trim().toUpperCase();
      const directorPanValue =
        name === "doc_directorPan_number"
          ? value.trim().toUpperCase()
          : (entityDocInputs["doc_directorPan_number"] || "")
              .trim()
              .toUpperCase();

      if (
        (name === "doc_pan_number" || name === "doc_directorPan_number") &&
        panValue &&
        directorPanValue &&
        panValue === directorPanValue
      ) {
        e["doc_pan_number"] = "PAN Card and Director PAN must not be the same";
        e["doc_directorPan_number"] =
          "Director PAN must be different from PAN Card";
      } else {
        // Clear the cross-check errors if they no longer apply
        if (
          e["doc_pan_number"] ===
          "PAN Card and Director PAN must not be the same"
        ) {
          delete e["doc_pan_number"];
        }
        if (
          e["doc_directorPan_number"] ===
          "Director PAN must be different from PAN Card"
        ) {
          delete e["doc_directorPan_number"];
        }
      }

      // PAN entity type check — for main entity PAN and Trust PAN
      const panEntityFields = ["doc_pan_number", "doc_panTrust_number"];
      if (panEntityFields.includes(name) && formData.entityType) {
        const entityCheck = validatePANEntityType(value, formData.entityType);
        if (!entityCheck.isValid) {
          e[name] = entityCheck.message;
          toast.error(`⚠️ PAN Mismatch: ${entityCheck.message}`, {
            position: "top-right",
            autoClose: 6000,
            toastId: "pan-entity-mismatch",
          });
        }
      }

      // GST-PAN check on blur
      if (
        panEntityFields.includes(name) &&
        formData.isGSTRegistered === "yes"
      ) {
        const panUpper = value.trim().toUpperCase();
        if (panUpper.length === 10) {
          const activeGstEntries = gstEntries.filter(
            (g) => g.gstNumber && g.gstNumber.trim().length === 15,
          );
          if (activeGstEntries.length > 0) {
            const multiCheck = validateMultipleGSTWithPAN(
              activeGstEntries,
              panUpper,
            );
            if (!multiCheck.isValid && multiCheck.errors.length > 0) {
              e[name] = multiCheck.errors[0].message;
              toast.error(`⚠️ ${multiCheck.errors[0].message}`, {
                position: "top-right",
                autoClose: 6000,
                toastId: "gst-pan-mismatch",
              });
              setGstErrors((prev) => {
                const next = [...prev];
                multiCheck.errors.forEach((err) => {
                  const idx = gstEntries.findIndex(
                    (g) => g.gstNumber === err.gstNumber,
                  );
                  if (idx !== -1) {
                    next[idx] = err.message;
                  }
                });
                return next;
              });
            } else {
              if (e[name] && e[name].includes("GST-PAN mismatch")) {
                delete e[name];
              }
              setGstErrors((prev) =>
                prev.map((err) =>
                  err && err.includes("GST-PAN mismatch") ? "" : err,
                ),
              );
            }
          }
        }
      }

      return e;
    });
  };

  const handleBankFileChange = (key, file) => {
    if (!file) return;
    setBankFiles((prev) => ({ ...prev, [key]: file }));
  };

  // const departmentOptions = useDepartmentOptions(); // TODO: use when department dropdown is added
  const [beneficiaryMatchStatus, setBeneficiaryMatchStatus] = useState(null);
  // null = not checked yet | "match" | "mismatch"

  const showThirdParty =
    formData.hasBankDetails === "yes" &&
    formData.tradeName.trim() !== "" &&
    formData.beneficiaryName.trim() !== "" &&
    formData.tradeName.trim().toLowerCase() !==
      formData.beneficiaryName.trim().toLowerCase();

  // URL params from step 1
  const entityCategory = searchParams.get("entityCategory") || "";
  // const entityLOBType = searchParams.get("entityLOBType") || "";         // TODO: show in summary
  // const lobNationality = searchParams.get("lobNationality") || "Domestic"; // TODO: include in payload when needed
  // const aaDepartmentFunction = searchParams.get("aaDepartmentFunction") || ""; // TODO: include in payload when needed
  const contactPersonEmail = searchParams.get("contactPersonEmail") || "";
  const spocEmailAddress = searchParams.get("spocEmailAddress") || "";
  const entityNameFromUrl = searchParams.get("EntityName") || "";
  const onboardingIdFromUrl =
    searchParams.get("onboardingId") ||
    searchParams.get("entityPrimaryId") ||
    "";

  const entityId =
    searchParams.get("entityId") || searchParams.get("EntityId") || "";

  const modifiedbyFromURL = searchParams.get("type");
  // console.log("MODIFIED : ", modifiedbyFromURL);

  // ── Fetch existing KYC data by onboardingId from URL ──────────────────────
  useEffect(() => {
    if (!onboardingIdFromUrl) return;
    const id = parseInt(onboardingIdFromUrl, 10);
    if (!id) return;

    fetchKYCDetailsByIdAction(id).then(
      ({ success, data: record, documents }) => {
        if (!success || !record) return;

        // ── Nested sub-objects ──────────────────────────────────────────────
        const prop = record.proprietorshipDetails || {};
        const partner = record.partnershipDetails || {};
        const llp = record.llpDetails || {};
        const pvt = record.privatePublicLimitedCompanyDetails || {};
        const trust = record.trustDetails || {};
        const msme = record.msmeDetails || {};
        const utility = record.utilityBillDetails || {};
        const bank = record.bankDetails || {};
        const contact = record.contactPersonDetails || {};
        const pan = record.panDetails || {};
        const aadhar = record.adharDetails || {};

        setExistingDocuments(documents || []);
        setExistingKycId(record.kycId || 0);

        // Parse deficiency flags from the raw record
        const { deficiencyMap: dMap, isDeficiencyMode: dMode } =
          parseDeficiencies(record);
        // console.log("[Deficiency] dMap:", dMap, "| isDeficiencyMode:", dMode);
        setDeficiencyMap(dMap);
        setIsDeficiencyMode(dMode);

        // Seed existing paths for fallback when user doesn't re-upload
        setExistingPaths({
          AadharCardPath: prop.aadharCardPath || aadhar.aadharCardPath || "",
          AadharCardBackPath:
            prop.aadharCardBackPath ||
            aadhar.aadharCardBackPath ||
            llp.aadharCardBackPath ||
            partner.aadharCardBackPath ||
            "",
          aadharCardBackPath:
            prop.aadharCardBackPath ||
            aadhar.aadharCardBackPath ||
            llp.aadharCardBackPath ||
            partner.aadharCardBackPath ||
            "",
          PANCardPath: prop.panCardPath || pan.panCardPath || "",
          PartnershipDeedPath:
            partner.partnershipDeedPath || llp.partnershipDeedPath || "",
          UploadLLPCertificatePath: llp.uploadLLPCertificatePath || "",
          AadharOfBothPartnersPath:
            partner.aadharOfBothPartnersPath ||
            llp.aadharOfBothPartnersPath ||
            "",
          AadharOfBothPartnersPathSecond:
            partner.aadharOfBothPartnersPathSecond ||
            llp.aadharOfBothPartnersPathSecond ||
            "",
          CertificateOfIncorporationPath:
            pvt.certificateOfIncorporationPath || "",
          DirectorPANPath: pvt.directorPANPath || "",
          DirectorAadharPath: pvt.directorAadharPath || "",
          MSMECertificatePath: msme.msmeCertificatePath || "",
          UtilityBillDocumentPath: utility.utilityBillDocumentPath || "",
          PassbookOrCancelledChequePath:
            bank.passbookOrCancelledChequePath || "",
          UploadSignedFormPath: bank.uploadSignedFormPath || "",
          TrustDeedPath: trust.trustDeedPath || "",
          PANOfTrustPath: trust.trustPanPath || "",
          AadharOfTrusteePath: trust.trusteeAadharPath || "",
          PANOfTrusteePath: trust.trusteePanPath || "",
        });

        // Seed isXxx flags exactly as received from the API — passed through unchanged in payload
        setExistingIsFlags({
          isPANCard: prop.isPANCard ?? pan.isPANCard ?? null,
          isAadharCard: prop.isAadharCard ?? aadhar.isAadharCard ?? null,
          isPartnershipDeed:
            partner.isPartnershipDeed ?? llp.isPartnershipDeed ?? null,
          isAadharOfBothPartners:
            partner.isAadharOfBothPartners ??
            llp.isAadharOfBothPartners ??
            null,
          isLLPCertificate: llp.isLLPCertificate ?? null,
          isCertificateOfIncorporation:
            pvt.isCertificateOfIncorporation ?? null,
          isDirectorPan: pvt.isDirectorPan ?? null,
          isDirectorAadhar: pvt.isDirectorAadhar ?? null,
          isTrustDeed: trust.isTrustDeed ?? null,
          isPANOfTrust: trust.isPANOfTrust ?? null,
          isPANOfTrustee: trust.isPANOfTrustee ?? null,
          isAadharOfTrustee: trust.isAadharOfTrustee ?? null,
          isMSMECertificate: msme.isMSMECertificate ?? null,
          isUtilityBillDocument: utility.isUtilityBillDocument ?? null,
          isPassbookOrCancelledCheque: bank.isPassbookOrCancelledCheque ?? null,
          isSignedForm: bank.isSignedForm ?? null,
          // GST isGST flags — keyed by index
          ...(Array.isArray(record.gstList)
            ? Object.fromEntries(
                record.gstList.map((g, i) => [`isGST_${i}`, g.isGST ?? null]),
              )
            : {}),
        });

        const boolToYesNo = (v) =>
          v === true ? "yes" : v === false ? "no" : "";

        setFormData((prev) => ({
          ...prev,
          // Entity info
          parentName: record.entityName || entityNameFromUrl || prev.parentName,
          tradeName: record.tradeName || "",
          pinCode: record.pinCode || "",
          city: record.city || "",
          State: record.state || "",
          address: record.address || "",
          natureOfBusiness: record.natureOfBusiness || "",
          isInvoiceRequired: boolToYesNo(record.invoiceRequired),
          entityType: record.entityType || "",
          CompanypanNumber: prop.panCardNumber || pan.panCardNumber || "",
          // GST
          isGSTRegistered: boolToYesNo(record.isGSTRegistered),
          // Utility
          utilityType: utility.utilityType || "",
          utilityProviderName: utility.providerName || "",
          utilityAccountNumber: utility.accountOrConsumerNumber || "",
          utilityAddress: utility.addressOnUtilityBill || "",
          // MSME
          isMSMERegistered: boolToYesNo(msme.isRegisteredUnderMSME),
          msmeNumber: msme.msmeRegistrationNumber || "",
          msmeStatus: msme.msmeStatus || "",
          // Contact
          contactPersonName: contact.contactPersonName || "",
          contactPersonDesignation: contact.designation || "",
          contactPersonMobile: contact.mobileNumber || "",
          contactEmail: contact.contactPersonEmail || record.emailAddress || "",
          // Bank
          hasBankDetails: boolToYesNo(bank.provideBankDetails),
          bankName: bank.bankName || "",
          accountNumber: bank.accountNumber || "",
          ifscCode: bank.ifscCode || "",
          accountType: bank.accountType || "",
          branchName: bank.branchName || "",
          beneficiaryName: bank.beneficiaryName || "",
          relationWithBeneficiary: bank.relationWithBeneficiary || "",
          thirdPartyRelation: bank.relationWithBeneficiary || "",
        }));

        // Document number inputs
        setEntityDocInputs({
          // Aadhar number fields — NEVER auto-filled, always blank for security
          doc_aadhar_number: "",
          doc_aadharBoth_numbers: "",
          doc_aadharBothSecondary_numbers: "",
          doc_directorAadhar_number: "",
          doc_aadharTrustee_number: "",
          // All other fields auto-fill from API
          doc_pan_number: prop.panCardNumber || pan.panCardNumber || "",
          doc_llpCertificate_number: llp.llpCrtificateNumber || "",
          doc_partnershipDeed_ref: partner.partnershipDeedPath ? "" : "",
          doc_coi_number: pvt.certificateOfIncorporationNumber || "",
          doc_directorPan_number: pvt.directorPANNumber || "",
          doc_trustDeed_ref: trust.trustDeedNumber || "",
          doc_panTrust_number: trust.trustPanNumber || "",
          doc_panTrustee_number: trust.trusteePanNumber || "",
        });

        // GST entries
        if (Array.isArray(record.gstList) && record.gstList.length > 0) {
          setGstEntries(
            record.gstList.map((g) => ({
              gstId: g.gstId ?? 0, // preserve real gstId from API
              gstNumber: g.gstNumber || "",
              gstState: g.state || "",
              gstAddress: g.gstAddress || "",
            })),
          );
          setGstFiles(record.gstList.map(() => null));
          setGstErrors(record.gstList.map(() => ""));
          setExistingPaths((prev) => {
            const updated = { ...prev };
            record.gstList.forEach((g, i) => {
              if (g.gstCertificatePath)
                updated[`gst_file_${i}`] = g.gstCertificatePath;
            });
            return updated;
          });
        }
      },
    );
  }, [onboardingIdFromUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  // GST entry handlers
  const addGstEntry = () => {
    setGstEntries((prev) => [
      ...prev,
      { gstId: 0, gstNumber: "", gstState: "", gstAddress: "" },
    ]);
    setGstFiles((prev) => [...prev, null]);
    setGstErrors((prev) => [...prev, ""]);
  };
  const removeGstEntry = (index) => {
    setGstEntries((prev) => prev.filter((_, i) => i !== index));
    setGstFiles((prev) => prev.filter((_, i) => i !== index));
    setGstErrors((prev) => prev.filter((_, i) => i !== index));
  };
  const handleGstChange = (index, field, value) => {
    setGstEntries((prev) =>
      prev.map((entry, i) => {
        if (i !== index) return entry;
        const updated = {
          ...entry,
          [field]: field === "gstNumber" ? value.toUpperCase() : value,
        };
        if (field === "gstNumber") {
          updated.gstState = getStateFromGST(value.toUpperCase());
          updated.state = updated.gstState;
          setGstErrors((errs) => errs.map((e, ei) => (ei === index ? "" : e)));
        }
        if (field === "gstState") {
          updated.state = value;
        }
        return updated;
      }),
    );

    // Instant GST-PAN cross check if PAN is already entered
    if (field === "gstNumber") {
      const gstUpper = value.trim().toUpperCase();
      const currentPan = (
        entityDocInputs["doc_pan_number"] ||
        entityDocInputs["doc_panTrust_number"] ||
        formData.CompanypanNumber ||
        ""
      )
        .trim()
        .toUpperCase();

      const panKey = entityDocInputs["doc_panTrust_number"]
        ? "doc_panTrust_number"
        : "doc_pan_number";

      if (gstUpper.length < 15) {
        setGstErrors((prev) =>
          prev.map((e, ei) =>
            ei === index && e && e.includes("GST-PAN mismatch") ? "" : e,
          ),
        );
        setErrors((prev) => {
          const e = { ...prev };
          ["doc_pan_number", "doc_panTrust_number"].forEach((k) => {
            if (e[k] && e[k].includes("GST-PAN mismatch")) {
              delete e[k];
            }
          });
          return e;
        });
      } else if (gstUpper.length === 15 && currentPan.length === 10) {
        const panMatch = validateGSTPANMatch(gstUpper, currentPan);
        if (!panMatch.isValid) {
          setGstErrors((prev) =>
            prev.map((e, ei) => (ei === index ? panMatch.message : e)),
          );
          setErrors((prev) => ({ ...prev, [panKey]: panMatch.message }));
          toast.error(`GST Entry ${index + 1}: ${panMatch.message}`, {
            position: "top-right",
            autoClose: 6000,
            toastId: `gst-pan-mismatch-${index}`,
          });
        } else {
          setGstErrors((prev) => prev.map((e, ei) => (ei === index ? "" : e)));
          setErrors((prev) => {
            const e = { ...prev };
            ["doc_pan_number", "doc_panTrust_number"].forEach((k) => {
              if (e[k] && e[k].includes("GST-PAN mismatch")) {
                delete e[k];
              }
            });
            return e;
          });
        }
      }
    }
  };
  const handleGstBlur = (index) => {
    const gstNumber = gstEntries[index]?.gstNumber || "";
    if (!gstNumber) return;

    // First validate GST format
    const gstFormatResult = validateGST(gstNumber);
    if (gstFormatResult !== true) {
      setGstErrors((prev) =>
        prev.map((e, ei) => (ei === index ? gstFormatResult : e)),
      );
      return;
    }

    // Then validate GST-PAN match
    const companyPAN = (
      entityDocInputs["doc_pan_number"] ||
      entityDocInputs["doc_panTrust_number"] ||
      formData.CompanypanNumber ||
      ""
    )
      .trim()
      .toUpperCase();

    const panKey = entityDocInputs["doc_panTrust_number"]
      ? "doc_panTrust_number"
      : "doc_pan_number";

    if (companyPAN && companyPAN.length === 10) {
      const panMatchResult = validateGSTPANMatch(gstNumber, companyPAN);
      if (!panMatchResult.isValid) {
        setGstErrors((prev) =>
          prev.map((e, ei) => (ei === index ? panMatchResult.message : e)),
        );
        setErrors((prev) => ({ ...prev, [panKey]: panMatchResult.message }));
        toast.error(`GST Entry ${index + 1}: ${panMatchResult.message}`, {
          position: "top-right",
          autoClose: 8000,
          toastId: `gst-pan-mismatch-blur-${index}`,
        });
        return;
      } else {
        // Clear GST-PAN mismatch on PAN field
        setErrors((prev) => {
          const e = { ...prev };
          ["doc_pan_number", "doc_panTrust_number"].forEach((k) => {
            if (e[k] && e[k].includes("GST-PAN mismatch")) {
              delete e[k];
            }
          });
          return e;
        });
      }
    }

    // Clear error if all validations pass
    setGstErrors((prev) => prev.map((e, ei) => (ei === index ? "" : e)));
  };
  const handleGstFileChange = (index, file) => {
    if (!file) return;
    setGstFiles((prev) => prev.map((f, i) => (i === index ? file : f)));
  };

  // Seed entity name from URL param — used when no API prefill, or as initial value
  useEffect(() => {
    if (entityNameFromUrl) {
      setFormData((prev) => ({
        ...prev,
        // Only set if not already filled by API prefill
        parentName: prev.parentName || entityNameFromUrl,
      }));
    }
  }, [entityNameFromUrl]);

  // Pincode auto-fill
  // Pincode API auto-fill is disabled — state/city are now selected via dropdowns.
  // usePincodeAutofill({
  //   pincode: formData.pinCode,
  //   skipOnMount: !!(onboardingIdFromUrl && formData.city),
  //   onSuccess: ({ city, state }) => {
  //     setFormData((prev) => ({ ...prev, city, State: state }));
  //     setErrors((prev) => {
  //       const e = { ...prev };
  //       delete e.pinCode;
  //       return e;
  //     });
  //   },
  //   onError: (msg) => {
  //     setFormData((prev) => ({ ...prev, city: "", State: "" }));
  //     setErrors((prev) => ({ ...prev, pinCode: msg }));
  //   },
  // });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    let newValue = type === "checkbox" ? checked : value;

    const uppercaseFields = [
      "CompanypanNumber",
      "ifscCode",
      "thirdPartyIfscCode",
    ];
    if (uppercaseFields.includes(name) && typeof newValue === "string")
      newValue = newValue.toUpperCase();

    const digitsOnlyFields = [
      "contactPersonMobile",
      "accountNumber",
      "thirdPartyAccountNumber",
    ];
    if (digitsOnlyFields.includes(name) && typeof newValue === "string")
      newValue = newValue.replace(/\D/g, "");

    if (name === "pinCode") {
      newValue = newValue.replace(/\D/g, "").slice(0, 6);
    }

    if (name === "hasBankDetails" && newValue === "no") {
      setErrors((prev) => {
        const e = { ...prev };
        [
          "bankName",
          "accountNumber",
          "ifscCode",
          "accountType",
          "branchName",
          "beneficiaryName",
        ].forEach((f) => delete e[f]);
        return e;
      });
    }

    if (name === "isMSMERegistered" && newValue === "no") {
      setErrors((prev) => {
        const e = { ...prev };
        [
          "msmeNumber",
          "msmeStatus",
          "doc_msme_certificate",
          "msme_file",
        ].forEach((f) => delete e[f]);
        return e;
      });
      setFormData((prev) => ({
        ...prev,
        isMSMERegistered: "no",
        msmeNumber: "",
        msmeStatus: "",
      }));
      return;
    }

    if (name === "isGSTRegistered" && newValue === "yes") {
      setErrors((prev) => {
        const e = { ...prev };
        [
          "entity_doc_doc_pan",
          "entity_doc_doc_panTrust",
          "entity_doc_doc_aadhar",
          "entity_doc_aadharCardBackPath",
          "entity_doc_doc_aadharBoth",
          "entity_doc_doc_directorAadhar",
          "entity_doc_doc_aadharTrustee",
          "doc_aadhar_number",
          "doc_aadharBoth_numbers",
          "doc_aadharBothSecondary_numbers",
          "doc_directorAadhar_number",
          "doc_aadharTrustee_number",
        ].forEach((f) => delete e[f]);
        return e;
      });
    }

    setFormData((prev) => ({ ...prev, [name]: newValue }));

    if (name === "tradeName" || name === "beneficiaryName") {
      setBeneficiaryMatchStatus(null);
    }

    if (type !== "checkbox" && errors[name]) {
      setErrors((prev) => {
        const e = { ...prev };
        delete e[name];
        return e;
      });
    }

    // PAN entity type check — runs while typing once PAN is fully valid
    if (name === "CompanypanNumber" && type !== "checkbox") {
      const entityType = formData.entityType;
      if (entityType) {
        const entityCheck = validatePANEntityType(newValue, entityType);
        // console.log("[PAN Entity Check]", {
        //   pan: newValue,
        //   entityType,
        //   result: entityCheck,
        // });
        if (!entityCheck.isValid) {
          setErrors((prev) => ({ ...prev, [name]: entityCheck.message }));
          toast.error(`⚠️ PAN Mismatch: ${entityCheck.message}`, {
            position: "top-right",
            autoClose: 6000,
            toastId: "pan-entity-mismatch",
          });
        } else {
          // Clear the entity-type error once PAN matches again
          setErrors((prev) => {
            const e = { ...prev };
            if (
              e[name] &&
              e[name].includes("does not match selected entity type")
            ) {
              delete e[name];
            }
            return e;
          });
        }
      }
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    if (!value || !value.trim()) return;

    if (name === "beneficiaryName" || name === "tradeName") {
      const trade = (name === "tradeName" ? value : formData.tradeName)
        .trim()
        .toLowerCase();
      const beneficiary = (
        name === "beneficiaryName" ? value : formData.beneficiaryName
      )
        .trim()
        .toLowerCase();
      if (trade && beneficiary) {
        setBeneficiaryMatchStatus(trade === beneficiary ? "match" : "mismatch");
      }
      return;
    }

    const validators = {
      // Form fields
      CompanypanNumber: validatePAN,
      contactPersonMobile: validateMobile,
      ifscCode: validateIFSC,
      accountNumber: validateAccountNumber,
      thirdPartyIfscCode: validateIFSC,
      thirdPartyAccountNumber: validateAccountNumber,
      // Document number input fields (entityDocInputs)
      doc_aadhar_number: validateAadhar,
      doc_aadharBoth_numbers: validateAadhar,
      doc_aadharBothSecondary_numbers: validateAadhar,
      doc_aadharTrustee_number: validateAadhar,
      doc_directorAadhar_number: validateAadhar,
      doc_pan_number: validatePAN,
      doc_directorPan_number: validatePAN,
      doc_panTrust_number: validatePAN,
      doc_panTrustee_number: validatePAN,
    };
    const validator = validators[name];
    if (validator) {
      const result = validator(value);
      if (result !== true) {
        setErrors((prev) => ({ ...prev, [name]: result }));
        return;
      }
      setErrors((prev) => {
        const e = { ...prev };
        delete e[name];
        return e;
      });
    }

    // PAN entity type check — for CompanypanNumber field
    if (name === "CompanypanNumber" && formData.entityType) {
      const entityCheck = validatePANEntityType(value, formData.entityType);
      if (!entityCheck.isValid) {
        setErrors((prev) => ({ ...prev, [name]: entityCheck.message }));
      }
    }
  };

  const validate = () => {
    const newErrors = {};
    ALL_FIELDS.forEach((field) => {
      // Skip bank fields entirely when user chose not to provide bank details
      if (
        BANK_FIELDS.some((b) => b.name === field.name) &&
        formData.hasBankDetails !== "yes"
      )
        return;

      if (
        field.conditionalOn &&
        formData[field.conditionalOn] !== field.conditionalValue
      )
        return;
      const value = formData[field.name];
      if (field.required && (!value || value.toString().trim() === "")) {
        newErrors[field.name] = `${field.label} is required`;
      } else if (field.validation && value) {
        const result = field.validation(value);
        if (result !== true) newErrors[field.name] = result;
      }
    });

    // ── pinCode: optional, but if entered must be exactly 6 numeric digits ──
    const pin = (formData.pinCode || "").replace(/\D/g, "");
    if (pin && pin.length !== 6) {
      newErrors.pinCode = "Pin Code must be exactly 6 digits";
    }

    if (showThirdParty) {
      THIRD_PARTY_FIELDS.forEach((field) => {
        const value = formData[field.name];
        if (field.required && (!value || value.toString().trim() === "")) {
          newErrors[field.name] = `${field.label} is required`;
        } else if (field.validation && value) {
          const result = field.validation(value);
          if (result !== true) newErrors[field.name] = result;
        }
      });

      // Third-Party Declaration signed form is mandatory when third-party applies
      const hasThirdPartyDocUploaded =
        thirdPartyFile ||
        uploadedDocs["UploadSignedFormPath"] ||
        uploadedDocs["third_party_file"] ||
        existingDocuments.some(
          (d) =>
            d.key === "third_party_file" && d.files?.some((f) => f.isUploaded),
        ) ||
        existingPaths["UploadSignedFormPath"];

      if (!hasThirdPartyDocUploaded) {
        newErrors["third_party_file"] = "Third-Party Declaration is required";
      }
    }

    // MSME Certificate — mandatory when registered under MSME
    if (formData.isMSMERegistered === "yes") {
      const hasMsmeDocUploaded =
        msmeFile ||
        uploadedDocs["MSMECertificatePath"] ||
        uploadedDocs["doc_msme_certificate"] ||
        uploadedDocs["msme_file"] ||
        existingDocuments.some(
          (d) =>
            (d.key === "doc_msme_certificate" || d.key === "msme_file") &&
            d.files?.some((f) => f.isUploaded),
        ) ||
        existingPaths["MSMECertificatePath"];

      if (!hasMsmeDocUploaded) {
        newErrors["doc_msme_certificate"] = "MSME Certificate is required";
      }
    }

    // Entity-type document validation — required docs (PAN & Aadhaar)
    // When GST is Yes, PAN Card document and Aadhaar Card number & document become non-mandatory.
    // When GST is No or not selected, both PAN Card document and Aadhaar Card fields remain mandatory.
    if (formData.entityType) {
      const isGSTYes = formData.isGSTRegistered === "yes";
      const entityDocs = ENTITY_TYPE_DOCUMENTS[formData.entityType] || [];
      entityDocs.forEach((doc) => {
        const isAadharBackDoc =
          doc.key === "aadharCardBackPath" || doc.key === "doc_aadhar_back";

        const isAadharDoc =
          doc.key === "doc_aadhar" ||
          doc.key === "doc_aadharBoth" ||
          doc.key === "doc_directorAadhar" ||
          doc.key === "doc_aadharTrustee" ||
          /aadhar/i.test(doc.key || "") ||
          /aadhar/i.test(doc.label || "");

        const isPanDoc = doc.key === "doc_pan" || doc.key === "doc_panTrust";

        // Aadhaar back is ALWAYS non-mandatory.
        // When GST is Yes, PAN document and Aadhaar front document are non-mandatory.
        // When GST is No/unselected, PAN document and Aadhaar front document are mandatory.
        let isDocUploadRequired = doc.required;
        if (isAadharBackDoc) {
          isDocUploadRequired = false;
        } else if (isAadharDoc || isPanDoc) {
          isDocUploadRequired = isGSTYes ? false : doc.required;
        }

        const apiDocType = DOC_KEY_TO_API_TYPE[doc.key] || doc.key;
        // Check if file is uploaded (new upload or existing)
        const hasUploaded =
          entityFiles[doc.key] ||
          uploadedDocs[doc.key] ||
          uploadedDocs[apiDocType] ||
          existingPaths[apiDocType] ||
          existingPaths[doc.key] ||
          (doc.key === "aadharCardBackPath" &&
            (existingPaths?.AadharCardBackPath ||
              existingPaths?.aadharCardBackPath ||
              uploadedDocs?.AadharCardBackPath ||
              uploadedDocs?.aadharCardBackPath)) ||
          existingDocuments.some(
            (d) => d.key === doc.key && d.files?.some((f) => f.isUploaded),
          );

        if (isDocUploadRequired && !hasUploaded) {
          newErrors[`entity_doc_${doc.key}`] = `${doc.label} is required`;
        }

        // Also validate the number input if present
        if (doc.inputField) {
          const isAadharInput =
            [
              "doc_aadhar_number",
              "doc_aadharBoth_numbers",
              "doc_aadharBothSecondary_numbers",
              "doc_directorAadhar_number",
              "doc_aadharTrustee_number",
            ].includes(doc.inputField.name) ||
            /aadhar/i.test(doc.inputField.name);

          const isPrimaryPanInput =
            doc.inputField.name === "doc_pan_number" ||
            doc.inputField.name === "doc_panTrust_number";

          // PAN number is ALWAYS mandatory.
          // Aadhaar number is non-mandatory when GST is Yes, mandatory when GST is No/unselected.
          let isNumberInputRequired = doc.required;
          if (isPrimaryPanInput) {
            isNumberInputRequired = true;
          } else if (isAadharInput) {
            isNumberInputRequired = isGSTYes ? false : doc.required;
          }

          const inputVal =
            entityDocInputs[doc.inputField.name] ||
            (doc.inputField.name === "doc_pan_number"
              ? formData.CompanypanNumber
              : "") ||
            "";

          if (isNumberInputRequired && !inputVal.trim()) {
            newErrors[doc.inputField.name] = `${doc.label} Number is required`;
          } else if (inputVal.trim()) {
            if (isAadharInput) {
              const res = validateAadhar(inputVal.trim());
              if (res !== true) newErrors[doc.inputField.name] = res;
            } else if (
              doc.inputField.name === "doc_pan_number" ||
              doc.inputField.name === "doc_panTrust_number" ||
              doc.inputField.name === "doc_directorPan_number" ||
              doc.inputField.name === "doc_panTrustee_number"
            ) {
              const res = validatePAN(inputVal.trim());
              if (res !== true) newErrors[doc.inputField.name] = res;
            }
          }
        }
      });
    }

    // GST certificate and GST numbers — required for each GST entry when GST is registered
    if (formData.isGSTRegistered === "yes") {
      const companyPAN = (
        entityDocInputs["doc_pan_number"] ||
        entityDocInputs["doc_panTrust_number"] ||
        formData.CompanypanNumber ||
        ""
      )
        .trim()
        .toUpperCase();

      const panFieldKey = entityDocInputs["doc_panTrust_number"]
        ? "doc_panTrust_number"
        : "doc_pan_number";

      gstEntries.forEach((entry, i) => {
        // Validate GST number required and format
        if (!entry.gstNumber || !entry.gstNumber.trim()) {
          newErrors[`gst_number_${i}`] = `GST Number for entry ${i + 1} is required`;
          setGstErrors((prev) =>
            prev.map((e, ei) => (ei === i ? "GST Number is required" : e)),
          );
        } else {
          const gstRes = validateGST(entry.gstNumber);
          if (gstRes !== true) {
            newErrors[`gst_number_${i}`] = gstRes;
            setGstErrors((prev) =>
              prev.map((e, ei) => (ei === i ? gstRes : e)),
            );
          }
        }

        // Check if GST certificate is uploaded
        const hasUploaded =
          uploadedDocs[`gst_file_${i}`]?.filePath ||
          existingPaths[`gst_file_${i}`] ||
          existingDocuments.some(
            (d) =>
              d.key === `gst_file_${i}` && d.files?.some((f) => f.isUploaded),
          );
        if (!hasUploaded) {
          newErrors[`gst_certificate_${i}`] =
            `GST Certificate for entry ${i + 1} (${entry.gstNumber || "GST #" + (i + 1)}) is required`;
        }

        // Validate GST-PAN match — skip in deficiency mode
        if (
          !isDeficiencyMode &&
          entry.gstNumber &&
          entry.gstNumber.trim().length === 15 &&
          companyPAN &&
          companyPAN.length === 10
        ) {
          const panMatchResult = validateGSTPANMatch(
            entry.gstNumber,
            companyPAN,
          );
          if (!panMatchResult.isValid) {
            newErrors[`gst_pan_match_${i}`] = panMatchResult.message;
            newErrors[panFieldKey] = panMatchResult.message;
            // Also set the GST error for that specific entry
            setGstErrors((prev) =>
              prev.map((e, ei) => (ei === i ? panMatchResult.message : e)),
            );
          }
        }
      });
    }

    // PAN entity type check — block submit if PAN doesn't match entity type
    // Skip in deficiency mode — PAN was already validated on initial submission
    if (!isDeficiencyMode) {
      const panForCheck =
        entityDocInputs["doc_pan_number"] ||
        formData.CompanypanNumber ||
        "";
      if (panForCheck && formData.entityType) {
        const entityCheck = validatePANEntityType(
          panForCheck,
          formData.entityType,
        );
        if (!entityCheck.isValid) {
          const panField = entityDocInputs["doc_pan_number"]
            ? "doc_pan_number"
            : formData.CompanypanNumber
              ? "CompanypanNumber"
              : "doc_pan_number";
          newErrors[panField] = entityCheck.message;
        }
      }

      // Trust PAN entity type check — PAN of Trust must also match entity type
      const trustPanForCheck = entityDocInputs["doc_panTrust_number"] || "";
      if (trustPanForCheck && formData.entityType) {
        const entityCheck = validatePANEntityType(
          trustPanForCheck,
          formData.entityType,
        );
        if (!entityCheck.isValid) {
          newErrors["doc_panTrust_number"] = entityCheck.message;
        }
      }
    }

    // console.log("[Validate] errors found:", newErrors);
    return newErrors;
  };

  // Checks if any newly selected File object in form state hasn't yet received a filePath from upload API
  const hasPendingFileWithoutPath = useMemo(() => {
    const getDocFilePath = (key) => {
      const storageKey = DOC_KEY_TO_API_TYPE[key] || key;
      return (
        uploadedDocs[storageKey]?.filePath ||
        (storageKey === "AadharCardBackPath"
          ? uploadedDocs["aadharCardBackPath"]?.filePath
          : storageKey === "aadharCardBackPath"
            ? uploadedDocs["AadharCardBackPath"]?.filePath
            : "") ||
        ""
      );
    };

    // 1. Entity files
    for (const [key, file] of Object.entries(entityFiles)) {
      if (file && !getDocFilePath(key)) {
        return true;
      }
    }

    // 2. GST files
    if (formData.isGSTRegistered === "yes") {
      for (let i = 0; i < gstFiles.length; i++) {
        if (gstFiles[i] && !uploadedDocs[`gst_file_${i}`]?.filePath) {
          return true;
        }
      }
    }

    // 3. Utility file
    if (
      formData.isGSTRegistered === "no" &&
      utilityFile &&
      !getDocFilePath("utility_file")
    ) {
      return true;
    }

    // 4. MSME file
    if (
      formData.isMSMERegistered === "yes" &&
      msmeFile &&
      !getDocFilePath("msme_file")
    ) {
      return true;
    }

    // 5. Bank files
    if (formData.hasBankDetails === "yes") {
      for (const [key, file] of Object.entries(bankFiles)) {
        if (file && !getDocFilePath(key)) {
          return true;
        }
      }
    }

    // 6. Third party file
    if (
      showThirdParty &&
      thirdPartyFile &&
      !getDocFilePath("third_party_file")
    ) {
      return true;
    }

    return false;
  }, [
    entityFiles,
    gstFiles,
    utilityFile,
    msmeFile,
    bankFiles,
    thirdPartyFile,
    uploadedDocs,
    formData.isGSTRegistered,
    formData.isMSMERegistered,
    formData.hasBankDetails,
    showThirdParty,
  ]);

  const handleSubmit = async (e, isSaveAsDraft = false) => {
    if (e && e.preventDefault) e.preventDefault();

    // Guard against in-flight uploads
    if (uploadingDocKeys.size > 0) {
      toast.warn("Please wait while your document(s) are uploading.", {
        toastId: "upload-in-progress",
      });
      return;
    }

    // Guard against pending or failed uploads without a valid filePath
    if (hasPendingFileWithoutPath || failedUploadKeys.size > 0) {
      toast.error(
        "One or more documents could not be uploaded or did not receive a valid file path. Please re-upload before submitting.",
        { autoClose: 6000, toastId: "upload-failed-pending" },
      );
      return;
    }

    // Skip validation for Save Draft
    if (!isSaveAsDraft) {
      const validationErrors = validate();
      if (Object.keys(validationErrors).length > 0) {
        setErrors(validationErrors);
        console.error("[Submit] Validation errors:", validationErrors);

        // Build a human-readable summary for the toast
        const errorMessages = Object.values(validationErrors);
        const firstError = errorMessages[0];
        const extraCount = errorMessages.length - 1;

        toast.error(
          extraCount > 0
            ? `${firstError} (+${extraCount} more error${extraCount > 1 ? "s" : ""})`
            : firstError,
          { autoClose: 6000 },
        );
        return;
      }
    }

    setIsSubmitting(true);
    try {
      const loggedInUser = getUserData();
      const now = new Date().toISOString();
      const entityPrimaryId = parseInt(
        searchParams.get("onboardingId") ||
          searchParams.get("entityPrimaryId") ||
          "0",
        10,
      );

      // Helper: get filePath — new upload takes priority, falls back to existing path from API
      const getPath = (documentType) =>
        uploadedDocs[documentType]?.filePath ||
        existingPaths[documentType] ||
        (documentType === "AadharCardBackPath"
          ? uploadedDocs["aadharCardBackPath"]?.filePath ||
            existingPaths["aadharCardBackPath"]
          : documentType === "aadharCardBackPath"
            ? uploadedDocs["AadharCardBackPath"]?.filePath ||
              existingPaths["AadharCardBackPath"]
            : "") ||
        "";

      const hasPath = (documentType) => !!getPath(documentType);

      // ── Debug: log all document paths before building payload ──────────────
      const ALL_DOC_KEYS = [
        "AadharCardPath",
        "AadharCardBackPath",
        "PANCardPath",
        "PartnershipDeedPath",
        "AadharOfBothPartnersPath",
        "AadharOfBothPartnersPathSecond",
        "UploadLLPCertificatePath",
        "CertificateOfIncorporationPath",
        "DirectorPANPath",
        "DirectorAadharPath",
        "MSMECertificatePath",
        "UtilityBillDocumentPath",
        "PassbookOrCancelledChequePath",
        "UploadSignedFormPath",
        "TrustDeedPath",
        "PANOfTrustPath",
        "AadharOfTrusteePath",
        "PANOfTrusteePath",
      ];
      // console.log("[Submit] Document paths resolved:");
      ALL_DOC_KEYS.forEach((key) => {
        const newPath = uploadedDocs[key]?.filePath;
        const existingPath = existingPaths[key];
        const resolved = getPath(key);
        //  console.log(
        //     `  ${key}: ${resolved ? `✅ "${resolved}"` : "❌ empty"}` +
        //       (newPath
        //         ? ` [new upload]`
        //         : existingPath
        //           ? ` [existing]`
        //           : ` [missing]`),
        // );
      });
      // GST certs
      gstEntries.forEach((_, i) => {
        const key = `gst_file_${i}`;
        const resolved =
          uploadedDocs[key]?.filePath || existingPaths[key] || "";
        // console.log(
        //   `  gst_file_${i}: ${resolved ? `✅ "${resolved}"` : "❌ empty"}`,
        // );
      });

      // Map from doc key (e.g. "doc_aadhar") to the storage key used in uploadedDocs
      // (e.g. "AadharCardPath"). If a new file was uploaded for a deficient doc,
      // its *Def flag should be cleared (set to false) on submit.
      const DOC_KEY_TO_STORAGE_KEY = {
        doc_aadhar: "AadharCardPath",
        aadharCardBackPath: "AadharCardBackPath",
        doc_aadhar_back: "AadharCardBackPath",
        doc_pan: "PANCardPath",
        doc_partnershipDeed: "PartnershipDeedPath",
        doc_aadharBoth: "AadharOfBothPartnersPath",
        doc_llpCertificate: "UploadLLPCertificatePath",
        doc_coi: "CertificateOfIncorporationPath",
        doc_directorPan: "DirectorPANPath",
        doc_directorAadhar: "DirectorAadharPath",
        doc_trustDeed: "TrustDeedPath",
        doc_panTrust: "PANOfTrustPath",
        doc_panTrustee: "PANOfTrusteePath",
        doc_aadharTrustee: "AadharOfTrusteePath",
        doc_msme_certificate: "MSMECertificatePath",
        doc_utility_bill: "UtilityBillDocumentPath",
        doc_bank_proof: "PassbookOrCancelledChequePath",
        third_party_file: "UploadSignedFormPath",
      };

      /**
       * Returns the *Def flag value for a doc key.
       * Always returns false on submit — deficiency is cleared when the user submits.
       */
      const getDefFlag = (_docKey) => false;

      /**
       * Returns the isXxx flag value for a doc.
       * - API returned a value (true/false) → pass that exact value
       * - No API value (new entity, first submit) → false
       */
      const getIsFlag = (flagName) => {
        const apiVal = existingIsFlags[flagName];
        return apiVal !== null && apiVal !== undefined ? apiVal : false;
      };

      // Build uploadedList — new uploads first, then existing paths not re-uploaded
      const newUploads = Object.values(uploadedDocs).map((doc) => ({
        success: true,
        message: "",
        id: doc.id ?? 0,
        fileName: doc.fileName || "",
        filePath: doc.filePath || "",
        documentType: doc.documentType || "",
        extension: doc.extension || "",
      }));

      // Add existing paths that were NOT re-uploaded in this session
      const newDocTypes = new Set(
        Object.values(uploadedDocs).map((d) => d.documentType),
      );
      const existingUploads = Object.entries(existingPaths)
        .filter(([key, path]) => path && !newDocTypes.has(key))
        .map(([key, path]) => ({
          success: true,
          message: "",
          id: 0,
          fileName: path.split(/[\\/]/).pop() || key,
          filePath: path,
          documentType: key,
          extension: path.includes(".") ? "." + path.split(".").pop() : "",
        }));

      const uploadedList = [...newUploads, ...existingUploads];

      // Safety net: ensure utility bill path is in uploadedList when GST is "no"
      // (covers edge case where upload API response hasn't been stored yet)
      if (formData.isGSTRegistered === "no") {
        const utilityPath = getPath("UtilityBillDocumentPath");
        const alreadyIncluded = uploadedList.some(
          (u) => u.filePath === utilityPath,
        );
        if (utilityPath && !alreadyIncluded) {
          uploadedList.push({
            success: true,
            message: "",
            id: 0,
            fileName: utilityPath.split(/[\\/]/).pop() || "UtilityBill",
            filePath: utilityPath,
            documentType: "UtilityBillDocumentPath",
            extension: utilityPath.includes(".")
              ? "." + utilityPath.split(".").pop()
              : "",
          });
        }
      }

      // Build gstList — each entry gets its own cert path from indexed slot
      const gstList = gstEntries.map((entry, i) => ({
        id: entry.gstId || i + 1, // use real gstId from API if available
        gstNumber: entry.gstNumber || "",
        state: entry.gstState || "",
        gstAddress: entry.gstAddress || "",
        gstCertificatePath:
          uploadedDocs[`gst_file_${i}`]?.filePath ||
          existingPaths[`gst_file_${i}`] ||
          "",
      }));

      const payload = {
        // ── Identifiers ───────────────────────────────────────────────────────
        entityPrimaryId,
        kycId: existingKycId || 0,
        entityName: formData.parentName || "",
        tradeName: formData.tradeName || "",
        pinCode: formData.pinCode || "",
        city: formData.city || "",
        state: formData.State || "",
        address: formData.address || "",
        natureOfBusiness: formData.natureOfBusiness || "",
        invoiceRequired:
          formData.isInvoiceRequired === "yes"
            ? true
            : formData.isInvoiceRequired === "no"
              ? false
              : null,
        entityType: formData.entityType || "",

        // ── Contact person ────────────────────────────────────────────────────
        contactPersonDetails: {
          contactPersonName: formData.contactPersonName || "",
          designation: formData.contactPersonDesignation || "",
          mobileNumber: formData.contactPersonMobile || "",
          contactPersonEmail: formData.contactEmail || "",
        },

        // ── PAN ───────────────────────────────────────────────────────────────
        panDetails: {
          panCardNumber: entityDocInputs["doc_pan_number"] || "",
          panCardPath: getPath("PANCardPath") || "",
          panCardUploaded: hasPath("PANCardPath"),
          isPANCard: getIsFlag("isPANCard"),
          isPANCardDef: getDefFlag("doc_pan"),
        },

        // ── Aadhar ────────────────────────────────────────────────────────────
        adharDetails: {
          aadharCardNumber: entityDocInputs["doc_aadhar_number"] || "",
          aadharCardPath: getPath("AadharCardPath") || "",
          aadharCardUploaded: hasPath("AadharCardPath"),
          aadharCardBackPath:
            getPath("AadharCardBackPath") ||
            getPath("aadharCardBackPath") ||
            "",
          aadharCardBackUploaded:
            hasPath("AadharCardBackPath") || hasPath("aadharCardBackPath"),
          isAadharCard: getIsFlag("isAadharCard"),
          isAadharCardDef: getDefFlag("doc_aadhar"),
        },

        // ── Proprietorship ────────────────────────────────────────────────────
        proprietorshipDetails: {
          aadharCardNumber: entityDocInputs["doc_aadhar_number"] || "",
          aadharCardPath: getPath("AadharCardPath") || "",
          aadharCardUploaded: hasPath("AadharCardPath"),
          aadharCardBackPath:
            getPath("AadharCardBackPath") ||
            getPath("aadharCardBackPath") ||
            "",
          aadharCardBackUploaded:
            hasPath("AadharCardBackPath") || hasPath("aadharCardBackPath"),
          isAadharCard: getIsFlag("isAadharCard"),
          isAadharCardDef: getDefFlag("doc_aadhar"),
          panCardNumber: entityDocInputs["doc_pan_number"] || "",
          panCardPath: getPath("PANCardPath") || "",
          panCardUploaded: hasPath("PANCardPath"),
          isPANCard: getIsFlag("isPANCard"),
          isPANCardDef: getDefFlag("doc_pan"),
        },

        // ── Partnership ───────────────────────────────────────────────────────
        partnershipDetails: {
          partnershipDeedPath: getPath("PartnershipDeedPath") || "",
          partnershipDeedUploaded: hasPath("PartnershipDeedPath"),
          isPartnershipDeed: getIsFlag("isPartnershipDeed"),
          isPartnershipDeedDef: getDefFlag("doc_partnershipDeed"),
          aadharNumberOfBothPartners:
            entityDocInputs["doc_aadharBoth_numbers"] || "",
          aadharOfBothPartnersPath: getPath("AadharOfBothPartnersPath") || "",
          aadharOfBothPartnersUploaded: hasPath("AadharOfBothPartnersPath"),
          isAadharOfBothPartners: getIsFlag("isAadharOfBothPartners"),
          isAadharOfBothPartnersDef: getDefFlag("doc_aadharBoth"),
          aadharNumberOfBothPartnersSecond:
            entityDocInputs["doc_aadharBothSecondary_numbers"] || "",
          aadharOfBothPartnersPathSecond:
            getPath("AadharOfBothPartnersPathSecond") || "",
          aadharOfBothPartnersUploadedSecond: hasPath(
            "AadharOfBothPartnersPathSecond",
          ),
          isAadharOfBothPartnersSecond: false,
          isAadharOfBothPartnersDefSecond: getDefFlag(
            "doc_aadharBothSecondary",
          ),
          aadharCardBackPath:
            getPath("AadharCardBackPath") ||
            getPath("aadharCardBackPath") ||
            "",
          aadharCardBackUploaded:
            hasPath("AadharCardBackPath") || hasPath("aadharCardBackPath"),
        },

        // ── LLP ─────────────────────────────────────────────────────────────── ───────────────────────────────────────────────────────────────
        llpDetails: {
          llpCrtificateNumber:
            entityDocInputs["doc_llpCertificate_number"] || "",
          uploadLLPCertificatePath: getPath("UploadLLPCertificatePath") || "",
          llpCertificateUploaded: hasPath("UploadLLPCertificatePath"),
          isLLPCertificate: getIsFlag("isLLPCertificate"),
          isLLPCertificateDef: getDefFlag("doc_llpCertificate"),
          partnershipDeedPath: getPath("PartnershipDeedPath") || "",
          partnershipDeedUploaded: hasPath("PartnershipDeedPath"),
          isPartnershipDeed: getIsFlag("isPartnershipDeed"),
          isPartnershipDeedDef: getDefFlag("doc_partnershipDeed"),
          aadharNumberOfBothPartners:
            entityDocInputs["doc_aadharBoth_numbers"] || "",
          aadharOfBothPartnersPath: getPath("AadharOfBothPartnersPath") || "",
          aadharOfBothPartnersUploaded: hasPath("AadharOfBothPartnersPath"),
          isAadharOfBothPartners: getIsFlag("isAadharOfBothPartners"),
          isAadharOfBothPartnersDef: getDefFlag("doc_aadharBoth"),
          aadharNumberOfBothPartnersSecond:
            entityDocInputs["doc_aadharBothSecondary_numbers"] || "",
          aadharOfBothPartnersPathSecond:
            getPath("AadharOfBothPartnersPathSecond") || "",
          aadharOfBothPartnersUploadedSecond: hasPath(
            "AadharOfBothPartnersPathSecond",
          ),
          isAadharOfBothPartnersSecond: false,
          isAadharOfBothPartnersDefSecond: getDefFlag(
            "doc_aadharBothSecondary",
          ),
          aadharCardBackPath:
            getPath("AadharCardBackPath") ||
            getPath("aadharCardBackPath") ||
            "",
          aadharCardBackUploaded:
            hasPath("AadharCardBackPath") || hasPath("aadharCardBackPath"),
        },

        // ── Private / Public Limited ──────────────────────────────────────────
        privatePublicLimitedCompanyDetails: {
          certificateOfIncorporationNumber:
            entityDocInputs["doc_coi_number"] || "",
          certificateOfIncorporationPath:
            getPath("CertificateOfIncorporationPath") || "",
          certificateOfIncorporationUploaded: hasPath(
            "CertificateOfIncorporationPath",
          ),
          isCertificateOfIncorporation: getIsFlag(
            "isCertificateOfIncorporation",
          ),
          isCertificateOfIncorporationDef: getDefFlag("doc_coi"),
          directorPANNumber: entityDocInputs["doc_directorPan_number"] || "",
          directorPANPath: getPath("DirectorPANPath") || "",
          directorPanUploaded: hasPath("DirectorPANPath"),
          isDirectorPan: getIsFlag("isDirectorPan"),
          isDirectorPanDef: getDefFlag("doc_directorPan"),
          directorAadharNumber:
            entityDocInputs["doc_directorAadhar_number"] || "",
          directorAadharPath: getPath("DirectorAadharPath") || "",
          directorAadharUploaded: hasPath("DirectorAadharPath"),
          isDirectorAadhar: getIsFlag("isDirectorAadhar"),
          isDirectorAadharDef: getDefFlag("doc_directorAadhar"),
        },

        // ── Trust ─────────────────────────────────────────────────────────────
        trustDetails: {
          trustDeedNumber: entityDocInputs["doc_trustDeed_ref"] || "",
          trustDeedPath: getPath("TrustDeedPath") || "",
          trustDeedUploaded: hasPath("TrustDeedPath"),
          isTrustDeed: getIsFlag("isTrustDeed"),
          isTrustDeedDef: getDefFlag("doc_trustDeed"),
          trustPanNumber: entityDocInputs["doc_panTrust_number"] || "",
          trustPanPath: getPath("PANOfTrustPath") || "",
          trustPanUploaded: hasPath("PANOfTrustPath"),
          isPANOfTrust: getIsFlag("isPANOfTrust"),
          isPANOfTrustDef: getDefFlag("doc_panTrust"),
          trusteePanNumber: entityDocInputs["doc_panTrustee_number"] || "",
          trusteePanPath: getPath("PANOfTrusteePath") || "",
          trusteePanUploaded: hasPath("PANOfTrusteePath"),
          isPANOfTrustee: getIsFlag("isPANOfTrustee"),
          isPANOfTrusteeDef: getDefFlag("doc_panTrustee"),
          trusteeAadharNumber:
            entityDocInputs["doc_aadharTrustee_number"] || "",
          trusteeAadharPath: getPath("AadharOfTrusteePath") || "",
          trusteeAadharUploaded: hasPath("AadharOfTrusteePath"),
          isAadharOfTrustee: getIsFlag("isAadharOfTrustee"),
          isAadharOfTrusteeDef: getDefFlag("doc_aadharTrustee"),
        },

        // ── MSME ──────────────────────────────────────────────────────────────
        msmeDetails: {
          isRegisteredUnderMSME: convertToBoolean(formData.isMSMERegistered),
          msmeRegistrationNumber:
            formData.isMSMERegistered === "yes"
              ? formData.msmeNumber || ""
              : "",
          msmeStatus:
            formData.isMSMERegistered === "yes"
              ? formData.msmeStatus || ""
              : "",
          msmeCertificatePath: getPath("MSMECertificatePath") || "",
          msmeCertificateUploaded: hasPath("MSMECertificatePath"),
          isMSMECertificate: getIsFlag("isMSMECertificate"),
          isMSMECertificateDef: getDefFlag("doc_msme_certificate"),
        },

        // ── Utility bill ──────────────────────────────────────────────────────
        utilityBillDetails: {
          utilityType: formData.utilityType || "",
          providerName: formData.utilityProviderName || "",
          accountOrConsumerNumber: formData.utilityAccountNumber || "",
          addressOnUtilityBill: formData.utilityAddress || "",
          utilityBillDocumentPath: getPath("UtilityBillDocumentPath") || "",
          utilityBillDocumentUploaded: hasPath("UtilityBillDocumentPath"),
          IsUtilityBillDocument: getIsFlag("isUtilityBillDocument"),
          isUtilityBillDocumentDef: getDefFlag("doc_utility_bill"),
        },

        // ── Bank details ──────────────────────────────────────────────────────
        bankDetails: {
          provideBankDetails: formData.hasBankDetails === "yes",
          bankName: formData.bankName || "",
          accountNumber: formData.accountNumber || "",
          ifscCode: formData.ifscCode || "",
          accountType: formData.accountType || "",
          branchName: formData.branchName || "",
          beneficiaryName: formData.beneficiaryName || "",
          relationWithBeneficiary: formData.thirdPartyRelation || "",
          passbookOrCancelledChequePath:
            getPath("PassbookOrCancelledChequePath") || "",
          passbookOrCancelledChequeUploaded: hasPath(
            "PassbookOrCancelledChequePath",
          ),
          isPassbookOrCancelledCheque: getIsFlag("isPassbookOrCancelledCheque"),
          isPassbookOrCancelledChequeDef: getDefFlag("doc_bank_proof"),
          uploadSignedFormPath: getPath("UploadSignedFormPath") || "",
          signedFormUploaded: hasPath("UploadSignedFormPath"),
          isSignedForm: getIsFlag("isSignedForm"),
          isSignedFormDef: getDefFlag("third_party_file"),
        },

        // ── GST ───────────────────────────────────────────────────────────────
        isGSTRegistered: convertToBoolean(formData.isGSTRegistered),
        gstList:
          formData.isGSTRegistered === "yes"
            ? gstList.map((g, i) => {
                // isGST flag: only true if API explicitly returned true, otherwise false
                const apiIsGstValue = existingIsFlags[`isGST_${i}`];
                const isGSTValue = apiIsGstValue === true ? true : false;

                return {
                  id: g.id || i + 1,
                  gstNumber: g.gstNumber || "",
                  state: g.state || "",
                  gstAddress: g.gstAddress || "",
                  gstCertificatePath: g.gstCertificatePath || "",
                  isGST: isGSTValue,
                  isGSTDef: getDefFlag(`gst_file_${i}`),
                };
              })
            : [],

        // ── Uploaded file list ────────────────────────────────────────────────
        uploadedList,

        // ── Metadata ─────────────────────────────────────────────────────────
        createdBy: modifiedbyFromURL || formData.parentName,
        createdOn: now,
        modifiedBy: modifiedbyFromURL || formData.parentName,
        modifiedOn: now,
        isDocumentUploaded: isSaveAsDraft ? false : true,
        isSaveAsDraft,
      };

      // console.log("[Submit] Payload:", payload);
      const result = await sendEntityKYCFormDataAction(payload);
      if (result?.success) {
        if (isSaveAsDraft) {
          toast.success(result.message || "Draft saved successfully!");
        } else {
          toast.success(result.message || "KYC submitted successfully!");
          const entityName =
            formData.parentName || searchParams.get("EntityName") || "";
          const entityId =
            searchParams.get("entityId") || searchParams.get("EntityId") || "";
          navigate(
            `/thank-you?entityName=${encodeURIComponent(entityName)}&entityId=${encodeURIComponent(entityId)}`,
          );
        }
      } else {
        toast.error(
          result?.message ||
            result?.error ||
            (isSaveAsDraft ? "Failed to save draft" : "Failed to submit KYC"),
          { autoClose: 6000 },
        );
      }
    } catch (err) {
      console.error("[Submit] error:", err);
      toast.error(err?.message || "An error occurred while submitting");
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Document upload handler ────────────────────────────────────────────────
  const handleDocumentUpload = async (docKey, file) => {
    if (!file) return;

    // gst_file_0, gst_file_1 etc. → API type is always GSTCertificatePath
    // but we store the response keyed by the indexed slot so each entry is separate
    const isIndexedGst = /^gst_file_\d+$/.test(docKey);
    const documentType = isIndexedGst
      ? "GSTCertificatePath"
      : DOC_KEY_TO_API_TYPE[docKey] || docKey;
    // Storage key: use the full indexed key so each GST cert is stored separately
    const storageKey = isIndexedGst ? docKey : documentType;

    const entityId =
      searchParams.get("entityId") || searchParams.get("EntityId") || "";

    // console.log(
    //   `[Upload] Starting: docKey="${docKey}" → documentType="${documentType}" → storageKey="${storageKey}" | file="${file.name}"`,
    // );
    // Track active upload
    setUploadingDocKeys((prev) => new Set(prev).add(docKey));
    setFailedUploadKeys((prev) => {
      const next = new Set(prev);
      next.delete(docKey);
      return next;
    });

    // Invalidate stale filePath while uploading new file
    setUploadedDocs((prev) => {
      const updated = { ...prev };
      delete updated[storageKey];
      if (
        storageKey === "AadharCardBackPath" ||
        storageKey === "aadharCardBackPath"
      ) {
        delete updated.AadharCardBackPath;
        delete updated.aadharCardBackPath;
      }
      return updated;
    });

    try {
      const result = await uploadOnboardingDocumentAction({
        entityId,
        id: 0,
        documentType,
        fileName: file.name.replace(/\.[^/.]+$/, ""),
        file,
      });

      if (result.success && result.data && result.data.filePath) {
        setUploadedDocs((prev) => {
          const updated = {
            ...prev,
            [storageKey]: { ...result.data, documentType },
            ...(storageKey === "AadharCardBackPath" ||
            storageKey === "aadharCardBackPath"
              ? {
                  AadharCardBackPath: {
                    ...result.data,
                    documentType: "AadharCardBackPath",
                  },
                  aadharCardBackPath: {
                    ...result.data,
                    documentType: "AadharCardBackPath",
                  },
                }
              : {}),
          };
          return updated;
        });
        setFailedUploadKeys((prev) => {
          const next = new Set(prev);
          next.delete(docKey);
          return next;
        });
        return result;
      } else {
        console.error(
          `[Upload] ❌ FAILED: docKey="${docKey}" | error="${result?.error || "No file path returned"}"`,
        );
        setFailedUploadKeys((prev) => new Set(prev).add(docKey));
        toast.error(
          `Failed to upload ${documentType}: ${result?.error || "Unable to retrieve file path"}`,
        );
        return result;
      }
    } catch (err) {
      console.error(`[Upload] ❌ Exception: docKey="${docKey}"`, err);
      setFailedUploadKeys((prev) => new Set(prev).add(docKey));
      toast.error(
        `Failed to upload ${documentType}: ${err?.message || "Upload error"}`,
      );
      return { success: false, error: err?.message };
    } finally {
      setUploadingDocKeys((prev) => {
        const next = new Set(prev);
        next.delete(docKey);
        return next;
      });
    }
  };

  // ── Aadhar Visibility with OTP ────────────────────────────────────────────────
  const {
    otpModalOpen,
    currentAadharField,
    adminEmail,
    entityPrimaryId: aadharEntityId,
    handleEyeIconClick,
    handleOtpVerifySuccess,
    handleCloseOtpModal,
    getAadharDisplayValue,
    isAadharVisible,
    getCurrentFieldLabel,
    isAadharField,
  } = useAadharVisibilityData(
    entityDocInputs,
    parseInt(onboardingIdFromUrl, 10) || 0,
  );

  return {
    formData,
    errors,
    setErrors,
    isSubmitting,
    // departmentOptions, // TODO: re-enable when department dropdown is added
    handleChange,
    handleBlur,
    handleSubmit,
    handleSaveDraft: (e) => handleSubmit(e, true),
    handleDocumentUpload,
    existingDocuments,
    existingPaths,
    gstEntries,
    addGstEntry,
    removeGstEntry,
    handleGstChange,
    handleGstBlur,
    gstFiles,
    handleGstFileChange,
    gstErrors,
    utilityFile,
    setUtilityFile,
    msmeFile,
    setMsmeFile,
    entityId,
    thirdPartyFile,
    setThirdPartyFile,
    entityFiles,
    handleEntityFileChange,
    entityDocInputs,
    handleEntityDocInputChange,
    handleEntityDocInputBlur,
    bankFiles,
    handleBankFileChange,
    showThirdParty,
    beneficiaryMatchStatus,
    entityCategory,
    // entityLOBType,   // TODO: re-enable when shown in UI
    contactPersonEmail,
    spocEmailAddress,
    deficiencyMap,
    isDeficiencyMode,
    // Aadhar visibility
    otpModalOpen,
    currentAadharField,
    adminEmail,
    aadharEntityId,
    handleEyeIconClick,
    handleOtpVerifySuccess,
    handleCloseOtpModal,
    getAadharDisplayValue,
    isAadharVisible,
    getCurrentFieldLabel,
    isAadharField,
    // Upload state tracking
    uploadingDocKeys,
    isDocUploading,
    isUploadingDoc,
    uploadingDocCount,
    hasPendingFileWithoutPath,
    hasFailedUploads,
    failedUploadKeys,
  };
};
